import { reactive } from 'vue'
import type { Router, RouteLocationNormalizedLoaded } from 'vue-router'

import { router as currentRouter } from '@/helpers/router'
import { storage } from '@/helpers/storage'
import { StorageKeys } from '@/helpers/storage.keys'
import { guideCompletion19383Experiment } from '@/services/ab/experiments'
import type { GuideCompletion19383Variant } from '@/services/ab/types'
import { auth } from '@/services/auth'
import { Mixpanel } from '@/analytics/mixpanel'

/**
 * How the guide was completed: `explicit` is the Mark as Finished/Done button,
 * `passive` is reaching 100% while reading, and `audio` is reaching the end of
 * audio playback.
 */
export type CompletionPath = 'explicit' | 'passive' | 'audio'

export interface GuideCompletion {
  userId: string
  docId: string
  docUrlSlug: string
  completionPath: CompletionPath
}

type CompletionDoc = {
  id: string
  url_slug: string
}

/**
 * Tracks a guide completion until the completion popup is dismissed.
 *
 * The popup timing depends on how the user completed the guide. After the user
 * clicks Mark as Finished or Done, the popup starts loading before the resulting
 * navigation. When reading progress reaches 100%, it starts loading when the
 * user next navigates away from that guide. When audio playback ends, the popup
 * starts loading immediately.
 */
class CompletionService {
  private _completion: GuideCompletion | null = null
  private _shouldDisplay = false
  private _loadedUserId: string | null = null

  get current(): GuideCompletion | null {
    return this._shouldDisplay ? this._completion : null
  }

  /**
   * When the logged-in user changes, clear the previous user's in-memory state
   * and restore any pending completion stored for the current user.
   */
  private loadCompletionForCurrentUser(): void {
    const userId = auth.getUserId()
    if (userId === this._loadedUserId) {
      return
    }

    this._loadedUserId = userId
    this._completion = null
    this._shouldDisplay = false

    if (!userId) {
      return
    }

    const stored = storage.getItem(StorageKeys.guideCompletionPending19383)
    if (!stored) {
      return
    }

    const restored = JSON.parse(stored) as GuideCompletion
    if (restored.userId === userId) {
      this._completion = restored
    } else {
      storage.removeItem(StorageKeys.guideCompletionPending19383)
    }
  }

  /**
   * Check whether the user is still viewing the guide they completed by reading,
   * so its popup can be deferred until they navigate away from that guide.
   */
  private isViewingCompletedGuide(
    route: RouteLocationNormalizedLoaded,
    completion: GuideCompletion,
  ): boolean {
    return (
      String(route.name).startsWith('book.') &&
      route.params.url_slug === completion.docUrlSlug
    )
  }

  /**
   * Expose the stored completion. A guide completed by reading remains hidden
   * until the user navigates away from that guide.
   */
  private showCompletionIfReady(route: RouteLocationNormalizedLoaded): void {
    const pending = this._completion
    if (!pending || this._shouldDisplay) {
      return
    }

    if (
      pending.completionPath === 'passive' &&
      this.isViewingCompletedGuide(route, pending)
    ) {
      // After passive completion we leave the reader undisturbed while they remain
      // in this guide. The popup host starts loading its content when they navigate elsewhere.
      return
    }

    this._shouldDisplay = true
  }

  /**
   * Store a treatment user's completed guide in memory and local storage. Audio
   * and explicit completions start loading immediately. A passive completion
   * remains pending while the user is still viewing the completed guide.
   */
  recordCompletion(
    completedGuide: GuideCompletion,
    route: RouteLocationNormalizedLoaded,
  ): void {
    this.loadCompletionForCurrentUser()
    if (completedGuide.userId !== this._loadedUserId) {
      return
    }

    this._completion = completedGuide
    this._shouldDisplay = false
    storage.setItem(
      StorageKeys.guideCompletionPending19383,
      JSON.stringify(completedGuide),
    )

    this.showCompletionIfReady(route)
  }

  /**
   * Get or assign the user's guide-completion experiment variant when they
   * complete a guide. For treatment users, also store the completion for the
   * popup. Return the variant so Mark as Finished or Done can choose the correct
   * redirect.
   */
  async registerCompletion(
    doc: CompletionDoc,
    completionPath: CompletionPath,
  ): Promise<GuideCompletion19383Variant> {
    const userId = auth.getUserId()
    const variant = await guideCompletion19383Experiment()

    if (variant) {
      Mixpanel.trackCompletionExperimentGuideFinished(
        doc.id,
        completionPath,
        variant,
      )
    }

    if (variant === 'B' && userId) {
      this.recordCompletion(
        {
          userId,
          docId: doc.id,
          docUrlSlug: doc.url_slug,
          completionPath,
        },
        currentRouter().currentRoute.value,
      )
    }

    return variant
  }

  /**
   * Restore completion state for the current user and start loading the popup
   * when the given route is outside a passively completed guide.
   */
  onNavigation(route: RouteLocationNormalizedLoaded): void {
    this.loadCompletionForCurrentUser()
    this.showCompletionIfReady(route)
  }

  /**
   * Stop persisting a completion once its popup is visible. The in-memory
   * completion remains available until the popup closes.
   */
  markShown(): void {
    storage.removeItem(StorageKeys.guideCompletionPending19383)
  }

  /**
   * Close the popup and discard its in-memory and stored completion.
   */
  dismiss(): void {
    this._completion = null
    this._shouldDisplay = false
    storage.removeItem(StorageKeys.guideCompletionPending19383)
  }

  /**
   * Connect the completion service to router events so the popup host can start
   * loading before navigation finishes.
   *
   * When the app opens, restore any unseen completion from local storage and
   * decide whether to show it on the initial route.
   */
  initialize(router: Router): void {
    router.beforeResolve((to) => {
      this.onNavigation(to)
    })

    // Restore stored state once the initial route is known.
    router.isReady().then(() => {
      this.onNavigation(router.currentRoute.value)
    })
  }
}

export const completionService = reactive(new CompletionService())
